import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PdfViewerComponent } from '../../../../../../components/shared/pdf-viewer/pdf-viewer.component';
import {
  MIN_PAGE_WIDTH_SCALE,
  calculatePdfScale,
  IPdfPageDimensions,
} from 'src/app/utils/pdf-scale.utils';

import {
  QaiButtonComponent,
  QaiIconComponent,
  QaiLoadingComponent,
  QaiPageIndicatorComponent,
} from '@qai/ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AudioCallControlService,
  ISegmentProvider,
} from 'src/app/services/learner/audio-call-control.service';
import { IPdfProgress } from 'src/app/interfaces/ipdf-learn-state';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { CallStoreService } from '../../../library/call-store/call-store.service';
import { localeLowerCase } from '../../../../../../utils/locale-case.util';

const MIN_TIME_ON_LAST_PAGE_MS = 2000;
const ZOOM_EPSILON = 0.001;
const ZOOM_FACTOR = 1.25; // multiply/divide per step → constant 25% relative change each step
const NAV_SIDE_RESERVED_PX = 76; // 56px nav button + 20px ($spacing-2xl) margin, per side

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QaiButtonComponent,
    QaiIconComponent,
    QaiLoadingComponent,
    QaiPageIndicatorComponent,
    TranslocoModule,
    PdfViewerComponent,
  ],
  selector: 'app-call-pathway-pdf',
  standalone: true,
  styleUrls: ['./call-pathway-pdf.component.scss'],
  templateUrl: './call-pathway-pdf.component.html',
})
export class CallPathwayPdfComponent implements OnDestroy, ISegmentProvider {
  @ViewChild(PdfViewerComponent) pdfViewer!: PdfViewerComponent;
  readonly pdfUrl = input.required<string>();
  readonly enrollmentUuid = input.required<string>();
  readonly initialPage = input<number>(1);
  readonly isFirstAttempt = input<boolean>(true);
  readonly currentPage = signal(1);
  readonly totalPages = signal(0);
  readonly isLoaded = signal(false);
  // Incremented on each new pdfUrl to force <pdf-viewer> to destroy and
  // recreate, ensuring a clean ngOnInit (scrollMode, visibility, scale state)
  // instead of reusing a dirty instance when two consecutive steps are both PDF.
  protected readonly pdfViewerKey = signal(0);
  private pdfViewerKeyIsFirstRun = true;
  readonly isZoomedBeyondPageWidth = signal(false);
  readonly isScrollable = signal(false);
  readonly endCallTriggered = signal(false);
  private readonly scrolledPages = new Set<number>();
  private readonly currentPageScrolled = signal(false);
  private isNavigatingBackward = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly audioCallControlService = inject(AudioCallControlService);
  private readonly callStore = inject(CallStoreService);
  private audioCallStarted = false;
  private lastPageTimeout: any = null;
  readonly hasPageRendered = signal(false);
  readonly progressChange = output<IPdfProgress>();
  readonly pdfCompleted = output<void>();
  readonly isFullscreen = signal(false);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly fullscreenChangeHandler = () => {
    this.isFullscreen.set(!!document.fullscreenElement);
    this.cdr.markForCheck();
  };
  private readonly baseScale = signal<number | undefined>(undefined);
  private readonly userZoomMultiplier = signal(1.0);
  private minZoomMultiplier = 0;
  private maxZoomMultiplier = Infinity;
  private zoomFraction = 0;       // 0 = at min, 1 = at max
  private hasExplicitZoom = false; // true once user has explicitly zoomed on this PDF
  protected readonly customPdfScale = computed(() => {
    const base = this.baseScale();
    return base != null ? base * this.userZoomMultiplier() : undefined;
  });
  private isChangingPage = false;
  private pageChangeSnapshot = '';
  readonly callLearnUuid = input<string>('');
  private readonly segments: { page: number; text: string }[] = [];
  private readonly progressSubject = new Subject<{ page: number; total_pages: number }>();

  private readonly logger = inject(LoggerService);

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
  ) {
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.progressSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged((a, b) => a.page === b.page),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ page, total_pages }) => {
        this.emitProgress(page, total_pages);
      });

    effect(() => {
      const src = this.pdfUrl();
      this.isLoaded.set(false);
      this.endCallTriggered.set(false);
      this.hasPageRendered.set(false);
      this.isScrollable.set(false);
      this.userZoomMultiplier.set(1.0);
      this.baseScale.set(undefined);
      if (!this.pdfViewerKeyIsFirstRun) {
        this.pdfViewerKey.update(k => k + 1);
      }
      this.pdfViewerKeyIsFirstRun = false;
      this.hasExplicitZoom = false;
      this.zoomFraction = 0;
      this.audioCallStarted = false;
      this.scrolledPages.clear();
      this.currentPageScrolled.set(false);
      this.isNavigatingBackward = false;
      this.segments.length = 0;
      this.pageChangeSnapshot = '';
      if (!src) {
        this.currentPage.set(1);
        return;
      }
    });

    // Detect last page and trigger API
    effect(() => {
      const page = this.currentPage();
      const total = this.totalPages();
      const loaded = this.isLoaded();

      if (!loaded || total === 0 || this.endCallTriggered()) {
        this.clearLastPageTimer();
        return;
      }

      if (page === total) {
        // Hold the timer until the user has scrolled through the last page content
        if (this.isScrollGateLocked) {
          this.clearLastPageTimer();
          return;
        }
        this.startLastPageTimer();
      } else {
        this.clearLastPageTimer();
      }
    });

    effect(() => {
      const finalTranscript = this.callStore.getSpeechTranscript();
      const partialTranscript = this.callStore.getSpeechTranscriptPartial();
      const isLoaded = this.isLoaded();
    
      if (!isLoaded || this.isChangingPage) {
        return;
      }
    
      const baseTranscript = this.pageChangeSnapshot
        ? partialTranscript
        : finalTranscript;
    
      if (!baseTranscript?.trim()) {
        return;
      }
    
      const newText = this.getNewPortionSincePageChange(
        baseTranscript.trim()
      );
    
      if (newText) {
        this.updateCurrentSegment(newText);
      }
    });
  }

  private getNewPortionSincePageChange(transcript: string): string {
    if (!this.pageChangeSnapshot) {
      return transcript;
    }

    if(!transcript) {
      return '';
    }
  
    const committedText = this.pageChangeSnapshot;
  
    if (!committedText) {
      return transcript;
    }
  
    const locale = this.translocoService.getActiveLang() || 'en';
    // REMEDIATE: live speech-to-text transcript comparison
    const lowerTranscript = localeLowerCase(transcript, locale);
    // REMEDIATE: committed transcript text comparison
    const lowerCommitted = localeLowerCase(committedText, locale);
  
    if (lowerTranscript.startsWith(lowerCommitted)) {
      return transcript.slice(committedText.length).trim();
    }
  
    return transcript;
  }

  private updateCurrentSegment(text: string): void {
    const existingIndex = this.segments.findIndex(s => s.page === this.currentPage());
    if (existingIndex >= 0) {
      this.segments[existingIndex].text = text;
    } else {
      this.segments.push({ page: this.currentPage(), text });
    }
    // Remove any segments with empty text
    const emptyIndex = this.segments.findIndex(s => !s.text.trim());
    if (emptyIndex >= 0) this.segments.splice(emptyIndex, 1);
  }

  getUserSpokenResponse(): string {
    const transcriptFallback =
      (this.callStore.getSpeechTranscriptPartial() ?? '').trim() ||
      (this.callStore.getSpeechTranscript() ?? '').trim();

    if (this.segments.length === 0) {
      return transcriptFallback;
    }
    const userSpokenResponse = this.segments
      .map((s) => `${s.page} | ${s.text}`)
      .join('\n');
    // Clear segments for next utterance
    this.segments.length = 0;
    this.pageChangeSnapshot = '';
    return userSpokenResponse;
  }

  getCallLearnUuid(): string {
    return this.callLearnUuid() ?? '';
  }

  onPagesLoaded(total: number): void {
    this.totalPages.set(total);
    this.isLoaded.set(true);
    this.startAudioCallOnce();
    this.audioCallControlService.registerSegmentProvider(this);
    this.cdr.detectChanges();
    this.emitProgress(this.currentPage(), total);
    const startPage = this.initialPage();
    if (startPage > 1 && startPage <= total) {
      requestAnimationFrame(() => {
        if (this.pdfViewer) {
          this.pdfViewer.goToPage(startPage);
        }
      });
    }
  }

  private startAudioCallOnce(): void {
    if (this.audioCallStarted) return;
    this.audioCallStarted = true;
    this.callStore.setIsAudioCallMode(true);
    this.audioCallControlService.startCall().catch((err) => {
      console.error('Audio call start failed', err);
      this.audioCallStarted = false;
    });
  }

  onPageChange(page: number): void {
    if (this.isNavigatingBackward) {
      this.scrolledPages.add(page);
      this.isNavigatingBackward = false;
    }
    this.isChangingPage = true;
    this.commitSpeechToCurrentPage();
    this.currentPage.set(page);
    this.currentPageScrolled.set(this.scrolledPages.has(page));
    this.isChangingPage = false;
    this.progressSubject.next({ page, total_pages: this.totalPages() });
  }

  private commitSpeechToCurrentPage(): void {
    const partial = this.callStore.getSpeechTranscriptPartial() ?? '';
    if(!partial){
      this.segments.length = 0;
      this.pageChangeSnapshot = '';
      return;
    }
    const newPortion = this.getNewPortionSincePageChange(partial);
    if (newPortion) {
      this.updateCurrentSegment(newPortion);
      this.pageChangeSnapshot = partial;
    }
  }

  onPageRendered(): void {
    if (!this.isLoaded()) return;
    this.hasPageRendered.set(true);
  }

  onBeyondPageWidth(beyond: boolean): void {
    this.isZoomedBeyondPageWidth.set(beyond);
    this.cdr.markForCheck();
  }

  onIsScrollable(scrollable: boolean): void {
    this.isScrollable.set(scrollable);
  }

  onFirstScrolled(): void {
    this.scrolledPages.add(this.currentPage());
    this.currentPageScrolled.set(true);
  }

  previousPage(): void {
    if (this.canGoPrevious) {
      this.isNavigatingBackward = true;
      this.pdfViewer.goToPreviousPage();
    }
  }

  nextPage(): void {
    if (this.canGoNext) {
      this.pdfViewer.goToNextPage();
    }
  }

  get canGoPrevious(): boolean {
    return this.currentPage() > 1;
  }

  get isScrollGateLocked(): boolean {
    return this.isFirstAttempt() && this.isScrollable() && !this.currentPageScrolled();
  }

  get showScrollHint(): boolean {
    return this.isScrollGateLocked;
  }

  get canGoNext(): boolean {
    return this.currentPage() < this.totalPages() && !this.isScrollGateLocked;
  }

  private triggerEndCall(): void {
    this.handlePdfCompleted();
  }

  private startLastPageTimer(): void {
    if (this.lastPageTimeout || this.endCallTriggered()) return;

    this.lastPageTimeout = setTimeout(() => {
      this.endCallTriggered.set(true);
      this.triggerEndCall();
    }, MIN_TIME_ON_LAST_PAGE_MS);
  }

  private clearLastPageTimer(): void {
    if (this.lastPageTimeout) {
      clearTimeout(this.lastPageTimeout);
      this.lastPageTimeout = null;
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    this.progressSubject.complete();
    this.clearLastPageTimer();
    this.callStore.setIsAudioCallMode(false);
    this.audioCallControlService.unregisterSegmentProvider();
    this.audioCallControlService.endCall().catch((err) => {
      console.error('Audio call end failed', err);
    });
  }

  onPageDimensions(dims: IPdfPageDimensions): void {
    if (!dims.containerWidth || !dims.containerHeight) return;
    let scale = calculatePdfScale(dims.containerWidth, dims.containerHeight, dims.pdfWidth, dims.pdfHeight, dims.pageFitScale, this.logger);
    if (dims.pageWidthScale > MIN_PAGE_WIDTH_SCALE) {
      scale = Math.min(scale, dims.pageWidthScale);
    }
    this.baseScale.set(scale);

    if (scale > 0 && dims.pageFitScale > 0 && dims.pageWidthScale > 0) {
      const safeMinFitScale = (dims.containerHeight - 2) / dims.pdfHeight;
      const minFitScale = Math.min(dims.pageFitScale, safeMinFitScale);
      // Floor min at 1.0 so the default view (multiplier 1.0) is always reachable by zooming out.
      this.minZoomMultiplier = Math.max(0, Math.min(minFitScale / scale, 1));
      // Preferred max keeps the page clear of the nav buttons (÷ZOOM_FACTOR); floor it at one
      // zoom step above min-fit so the range can't collapse to a point, and cap at
      // pageWidthScale — the hard render cap in PdfViewerComponent.applyNonZoomedScale —
      // so every zoom-in click produces a visible change.
      const preferredMaxScale = this.isFullscreen()
        ? dims.pageWidthScale
        : dims.pageWidthScale / ZOOM_FACTOR;
      const effectiveMaxScale = Math.min(
        dims.pageWidthScale,
        Math.max(preferredMaxScale, minFitScale * ZOOM_FACTOR)
      );
      this.maxZoomMultiplier = Math.max(this.minZoomMultiplier, effectiveMaxScale / scale);

      if (this.hasExplicitZoom) {
        const range = this.maxZoomMultiplier - this.minZoomMultiplier;
        const remapped = range > 0
          ? this.minZoomMultiplier + this.zoomFraction * range
          : this.minZoomMultiplier;
        this.userZoomMultiplier.set(
          +Math.min(this.maxZoomMultiplier, Math.max(this.minZoomMultiplier, remapped)).toFixed(4)
        );
      }
    }
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.hostRef.nativeElement.requestFullscreen();
    }
  }

  zoomIn(): void {
    this.userZoomMultiplier.update(v => Math.min(+(v * ZOOM_FACTOR).toFixed(4), this.maxZoomMultiplier));
    this.saveZoomFraction();
  }

  zoomOut(): void {
    this.userZoomMultiplier.update(v => Math.max(+(v / ZOOM_FACTOR).toFixed(4), this.minZoomMultiplier));
    this.saveZoomFraction();
  }

  private saveZoomFraction(): void {
    const range = this.maxZoomMultiplier - this.minZoomMultiplier;
    this.zoomFraction = range > 0
      ? Math.min(1, Math.max(0, (this.userZoomMultiplier() - this.minZoomMultiplier) / range))
      : 0;
    this.hasExplicitZoom = true;
  }

  get canZoomIn(): boolean {
    return this.isLoaded() && this.userZoomMultiplier() < this.maxZoomMultiplier - ZOOM_EPSILON;
  }

  get canZoomOut(): boolean {
    return this.isLoaded() && this.userZoomMultiplier() > this.minZoomMultiplier + ZOOM_EPSILON;
  }

  private emitProgress(page: number, total_pages: number): void {
    this.progressChange.emit({ page, total_pages });
  }

  protected handlePdfCompleted(): void {
    this.pdfCompleted.emit();
  }
}